import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE } from "../utils/api";
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardHeader, 
  Grid, 
  Button, 
  Input, 
  TextField,
  FormLabel, 
  Snackbar, 
  Alert, 
  IconButton, 
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton
} from "@mui/material";
import { 
  CloudUpload as CloudUploadIcon, 
  Close as CloseIcon, 
  ImageSearch as ImageSearchIcon,
  Delete as DeleteIcon,
  Link as LinkIcon
} from "@mui/icons-material";

interface PromoBannerImage {
  id: number;
  type: string;
  filename: string;
  link: string | null;
  base64: string;
}

const BASE_URL = `${BASE}/api/promobanners`;

const lightPromoFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    color: "#111827 !important",
  },
  "& .MuiOutlinedInput-input": {
    color: "#111827 !important",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#111827 !important",
    opacity: 1,
  },
  "& .MuiInputAdornment-root .MuiSvgIcon-root": {
    color: "#111827",
  },
  "& .MuiOutlinedInput-root fieldset": {
    borderColor: "#d1d5db",
  },
  "& .MuiOutlinedInput-root:hover fieldset": {
    borderColor: "#25ee89",
  },
  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
    borderColor: "#25ee89",
  },
};

const PromoBanner: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [banners, setBanners] = useState<PromoBannerImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBanner, setSelectedBanner] = useState<{
    type: string;
    link: string;
    file: File | null;
    base64Preview?: string;
  } | null>(null);
  const [newBannerType, setNewBannerType] = useState('');
  const [newBannerLink, setNewBannerLink] = useState('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [linkEditDialog, setLinkEditDialog] = useState<{
    open: boolean;
    bannerId: number | null;
    currentLink: string;
  }>({ open: false, bannerId: null, currentLink: '' });

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/getAllImagesBinary`);
      if (response.data.status === 'success') {
        setBanners(response.data.images);
        setIsLoading(false);
      } else {
        handleSnackbarOpen('Failed to fetch promo banners', 'error');
        setIsLoading(false);
      }
    } catch (error) {
      handleSnackbarOpen('Failed to fetch promo banners', 'error');
      setIsLoading(false);
    }
  };

  const handleSnackbarOpen = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const handleBannerSelect = (banner: PromoBannerImage) => {
    setSelectedBanner({ 
      type: banner.type, 
      link: banner.link || '',
      file: null, 
      base64Preview: banner.base64 
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedBanner(prev => ({
          type: prev?.type || '',
          link: prev?.link || '',
          file: file,
          base64Preview: reader.result as string
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const createBanner = async () => {
    if (!selectedBanner || !selectedBanner.file) {
      handleSnackbarOpen('Please select an image', 'error');
      return;
    }
    if (!selectedBanner.type) {
      handleSnackbarOpen('Please enter a banner type', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('type', selectedBanner.type);
    formData.append('image', selectedBanner.file);
    if (selectedBanner.link) {
      formData.append('link', selectedBanner.link);
    }

    try {
      setIsLoading(true);
      await axios.post(`${BASE_URL}/createBanner`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      handleSnackbarOpen(`Promo Banner "${selectedBanner.type}" created successfully`);
      await fetchBanners();
      setSelectedBanner(null);
      setNewBannerType('');
      setNewBannerLink('');
    } catch (error) {
      handleSnackbarOpen('Failed to create promo banner', 'error');
      setIsLoading(false);
    }
  };

  const updateBanner = async () => {
    if (!selectedBanner || !selectedBanner.file) {
      handleSnackbarOpen('Please select a banner and an image', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('type', selectedBanner.type);
    formData.append('image', selectedBanner.file);
    if (selectedBanner.link) {
      formData.append('link', selectedBanner.link);
    }

    try {
      setIsLoading(true);
      await axios.post(`${BASE_URL}/updateBanner`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      handleSnackbarOpen(`Promo Banner ${selectedBanner.type} updated successfully`);
      await fetchBanners();
      setSelectedBanner(null);
    } catch (error) {
      handleSnackbarOpen('Failed to update promo banner', 'error');
      setIsLoading(false);
    }
  };

  const updateBannerLink = async () => {
    if (!linkEditDialog.bannerId) return;

    try {
      await axios.patch(`${BASE_URL}/updateLink/${linkEditDialog.bannerId}`, {
        link: linkEditDialog.currentLink
      });

      handleSnackbarOpen('Link updated successfully');
      await fetchBanners();
      setLinkEditDialog({ open: false, bannerId: null, currentLink: '' });
    } catch (error) {
      handleSnackbarOpen('Failed to update link', 'error');
    }
  };

  const openPreviewDialog = (base64: string) => {
    setPreviewImage(base64);
    setPreviewDialogOpen(true);
  };

  const openLinkEditDialog = (banner: PromoBannerImage) => {
    setLinkEditDialog({ open: true, bannerId: banner.id, currentLink: banner.link || '' });
  };

  const deleteBanner = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this promo banner?')) return;
    try {
      setIsLoading(true);
      await axios.delete(`${BASE_URL}/deleteBanner/${id}`);
      handleSnackbarOpen('Promo banner deleted successfully');
      await fetchBanners();
      setSelectedBanner(null);
    } catch (error) {
      handleSnackbarOpen('Failed to delete promo banner', 'error');
      setIsLoading(false);
    }
  };

  const renderBannerSkeletons = () => {
    return Array.from(new Array(6)).map((_, index) => (
      <Grid item xs={12} sm={6} md={4} key={index}>
        <Box>
          <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 2 }} />
          <Skeleton width="60%" sx={{ mx: 'auto', mt: 1 }} />
        </Box>
      </Grid>
    ));
  };

  return (
    <div className="ml-0 lg:ml-[256px] p-2">
      <Box sx={{ width: '100%', maxWidth: 1200, margin: 'auto', padding: 2 }}>
        <Card elevation={6} sx={{ borderRadius: 3 }}>
          <CardHeader 
            title="Promo Banner Management (with Links)" 
            titleTypographyProps={{ variant: 'h4', color: 'primary', align: 'center' }}
            sx={{ backgroundColor: theme.palette.background.paper, borderBottom: `1px solid ${theme.palette.divider}` }}
          />
          <CardContent>
            {/* Add New Banner Section */}
            <Box sx={{ mb: 4, p: 3, border: '2px dashed', borderColor: 'primary.main', borderRadius: 2, backgroundColor: 'grey.50' }}>
              <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>Add New Promo Banner</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  placeholder="Banner type (e.g., hero, promo, sports)"
                  value={newBannerType}
                  onChange={(e) => setNewBannerType(e.target.value)}
                  fullWidth
                  size="small"
                  sx={lightPromoFieldSx}
                />
                <TextField
                  placeholder="Navigation link (e.g., /sports, /casino, https://example.com)"
                  value={newBannerLink}
                  onChange={(e) => setNewBannerLink(e.target.value)}
                  fullWidth
                  size="small"
                  sx={lightPromoFieldSx}
                  InputProps={{ startAdornment: <LinkIcon sx={{ mr: 1, color: 'action.active' }} /> }}
                />
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Input 
                    type="file" 
                    inputProps={{ accept: '.png,.jpg,.jpeg,.webp', style: { display: 'none' } }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (e.target.files && e.target.files[0]) {
                        const file = e.target.files[0];
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setSelectedBanner({ type: newBannerType.trim(), link: newBannerLink.trim(), file, base64Preview: reader.result as string });
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    id="new-banner-file-input"
                  />
                  <label htmlFor="new-banner-file-input">
                    <Button component="span" variant="contained" color="primary" startIcon={<CloudUploadIcon />} disabled={!newBannerType.trim()}>
                      Choose Image
                    </Button>
                  </label>
                </Box>
              </Box>
              {selectedBanner?.base64Preview && selectedBanner?.file && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Type: <strong>{selectedBanner.type}</strong></Typography>
                  {selectedBanner.link && (
                    <Typography variant="body2" sx={{ mb: 1 }}>Link: <strong>{selectedBanner.link}</strong></Typography>
                  )}
                  <img src={selectedBanner.base64Preview} alt="Preview" style={{ maxWidth: 300, maxHeight: 200, borderRadius: 8 }} />
                  <Box sx={{ mt: 2 }}>
                    <Button variant="contained" color="success" onClick={createBanner}>Upload Promo Banner</Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Existing Banners Grid */}
            <Grid container spacing={3}>
              {isLoading ? renderBannerSkeletons() : banners.length === 0 ? (
                <Grid item xs={12}>
                  <Typography variant="body1" color="textSecondary" align="center" sx={{ py: 4 }}>
                    No promo banners yet. Add your first one above.
                  </Typography>
                </Grid>
              ) : banners.map(banner => (
                <Grid item xs={12} sm={6} md={4} key={banner.id}>
                  <Box sx={{ border: '2px solid', borderColor: selectedBanner?.type === banner.type ? 'primary.main' : 'grey.300', borderRadius: 2, padding: 1, cursor: 'pointer', backgroundColor: selectedBanner?.type === banner.type ? 'primary.light' : 'transparent', transition: 'all 0.3s ease', '&:hover': { boxShadow: theme.shadows[4], transform: 'scale(1.05)' } }}>
                    <Box onClick={() => handleBannerSelect(banner)} sx={{ position: 'relative' }}>
                      <img src={`data:image/png;base64,${banner.base64}`} alt={banner.type} style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 8 }} />
                      <IconButton onClick={(e) => { e.stopPropagation(); openPreviewDialog(banner.base64); }} sx={{ position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(255,255,255,0.7)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' } }}>
                        <ImageSearchIcon />
                      </IconButton>
                      <IconButton onClick={(e) => { e.stopPropagation(); deleteBanner(banner.id); }} sx={{ position: 'absolute', top: 8, left: 8, backgroundColor: 'rgba(255,100,100,0.8)', color: 'white', '&:hover': { backgroundColor: 'rgba(255,50,50,0.95)' } }}>
                        <DeleteIcon />
                      </IconButton>
                      <IconButton onClick={(e) => { e.stopPropagation(); openLinkEditDialog(banner); }} sx={{ position: 'absolute', bottom: 8, right: 8, backgroundColor: 'rgba(100,150,255,0.8)', color: 'white', '&:hover': { backgroundColor: 'rgba(100,150,255,0.95)' } }}>
                        <LinkIcon />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" align="center" sx={{ mt: 1, fontWeight: 'bold' }}>{banner.type}</Typography>
                    {banner.link && (
                      <Typography variant="caption" align="center" sx={{ display: 'block', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{banner.link}</Typography>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>

            {!isLoading && selectedBanner && (
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <FormLabel sx={{ display: 'block', mb: 2, fontWeight: 'bold' }}>Selected: {selectedBanner.type}</FormLabel>
                <TextField label="Link" value={selectedBanner.link} onChange={(e) => setSelectedBanner(prev => ({ ...prev!, link: e.target.value }))} fullWidth sx={{ mb: 2 }} />
                <Input type="file" inputProps={{ accept: '.png,.jpg,.jpeg,.webp', style: { display: 'none' } }} onChange={handleFileChange} id="banner-file-input" />
                <label htmlFor="banner-file-input">
                  <Button component="span" variant="contained" color="primary" startIcon={<CloudUploadIcon />} sx={{ mb: 2 }}>Choose File</Button>
                </label>
                {selectedBanner.base64Preview && (
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <img src={selectedBanner.base64Preview} alt="Preview" style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 8, boxShadow: theme.shadows[3] }} />
                  </Box>
                )}
                <Button onClick={updateBanner} disabled={!selectedBanner.file || isLoading} variant="contained" color="secondary" fullWidth sx={{ mt: 2, py: 1.5, borderRadius: 2 }}>
                  {isLoading ? 'Updating...' : 'Update Banner'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Banner Preview</DialogTitle>
          <DialogContent>
            <img src={`data:image/png;base64,${previewImage}`} alt="Full Preview" style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialogOpen(false)} color="primary">Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={linkEditDialog.open} onClose={() => setLinkEditDialog({ open: false, bannerId: null, currentLink: '' })} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Banner Link</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField label="Navigation Link" value={linkEditDialog.currentLink} onChange={(e) => setLinkEditDialog(prev => ({ ...prev, currentLink: e.target.value }))} fullWidth placeholder="/sports, /casino, https://example.com" InputProps={{ startAdornment: <LinkIcon sx={{ mr: 1, color: 'action.active' }} /> }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLinkEditDialog({ open: false, bannerId: null, currentLink: '' })} color="secondary">Cancel</Button>
            <Button onClick={updateBannerLink} color="primary" variant="contained">Update Link</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </div>
  );
};

export default PromoBanner;
